<script setup>
import { computed, nextTick, onActivated, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import glossaryAPI from 'dashboard/api/glossary';
import Button from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import CreateGlossaryDialog from './CreateGlossaryDialog.vue';

const { t } = useI18n();
const router = useRouter();

const isLoading = ref(false);
const records = ref([]);
const showCreateDialog = ref(false);
const createGlossaryDialog = ref(null);

const hasGlossaries = computed(() => records.value.length > 0);

const statusLabel = status => {
  const statusMap = {
    pending: t('GLOSSARY_SETTINGS.STATUS.PENDING'),
    synced: t('GLOSSARY_SETTINGS.STATUS.SYNCED'),
    failed: t('GLOSSARY_SETTINGS.STATUS.FAILED'),
  };

  return statusMap[status] || status || '-';
};

const validTerms = glossary =>
  (glossary.terms || []).filter(
    term => term.source_term?.trim() && term.target_term?.trim()
  );

const previewTerms = glossary => validTerms(glossary).slice(0, 3);

const remainingTermsCount = glossary =>
  Math.max(validTerms(glossary).length - previewTerms(glossary).length, 0);

const refreshGlossaries = async () => {
  isLoading.value = true;
  try {
    const response = await glossaryAPI.get();
    records.value = response.data.payload || [];
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('GLOSSARY_SETTINGS.MESSAGES.LOAD_FAILED')
    );
  } finally {
    isLoading.value = false;
  }
};

const openCreateDialog = async () => {
  showCreateDialog.value = true;
  await nextTick();
  createGlossaryDialog.value?.open();
};

const closeCreateDialog = () => {
  showCreateDialog.value = false;
};

const handleCreated = async () => {
  await refreshGlossaries();
};

const openDetail = glossaryId => {
  router.push({
    name: 'glossary_detail',
    params: {
      accountId: router.currentRoute.value.params.accountId,
      glossaryId,
    },
  });
};

onBeforeMount(() => {
  refreshGlossaries();
});

onActivated(() => {
  refreshGlossaries();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('GLOSSARY_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('GLOSSARY_SETTINGS.TITLE')"
        :description="$t('GLOSSARY_SETTINGS.DESCRIPTION')"
        feature-name="integrations"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('GLOSSARY_SETTINGS.ACTIONS.CREATE')"
            @click="openCreateDialog"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <section class="w-full rounded-2xl border border-n-weak bg-n-solid-1 p-5">
        <div
          class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ $t('GLOSSARY_SETTINGS.LIST_TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ $t('GLOSSARY_SETTINGS.LIST_DESCRIPTION') }}
            </p>
          </div>
        </div>

        <div v-if="hasGlossaries" class="mt-5 grid gap-3">
          <article
            v-for="glossary in records"
            :key="glossary.id"
            class="rounded-xl border border-n-weak bg-n-background p-4"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <h3 class="text-base font-medium text-n-slate-12">
                    {{ glossary.name }}
                  </h3>
                  <span
                    class="rounded-md border border-n-weak bg-n-solid-1 px-2 py-1 text-xs font-medium uppercase text-n-slate-11"
                  >
                    {{ glossary.source_language }}
                  </span>
                  <span class="i-lucide-arrow-right size-3 text-n-slate-11" />
                  <span
                    class="rounded-md border border-n-weak bg-n-solid-1 px-2 py-1 text-xs font-medium uppercase text-n-slate-11"
                  >
                    {{ glossary.target_language }}
                  </span>
                </div>
              </div>

              <Button
                slate
                :label="$t('GLOSSARY_SETTINGS.ACTIONS.GLOSSARY_DETAIL')"
                @click="openDetail(glossary.id)"
              />
            </div>

            <div
              class="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-n-slate-11"
            >
              <p>
                <span class="font-medium text-n-slate-12">
                  {{ $t('GLOSSARY_SETTINGS.FIELDS.SYNC_STATUS_LABEL') }}
                </span>
                {{ statusLabel(glossary.status) }}
              </p>
              <p
                v-if="glossary.last_sync_error"
                class="min-w-0 flex-1 text-n-ruby-11"
              >
                <span class="font-medium text-n-ruby-11">
                  {{ $t('GLOSSARY_SETTINGS.FIELDS.LAST_SYNC_ERROR_LABEL') }}
                </span>
                <span class="break-words">
                  {{ glossary.last_sync_error }}
                </span>
              </p>
            </div>

            <p class="mt-2 text-sm text-n-slate-11">
              <span class="font-medium text-n-slate-12">
                {{ $t('GLOSSARY_SETTINGS.FIELDS.TENCENT_GLOSSARY_ID_LABEL') }}
              </span>
              {{ glossary.tencent_glossary_id || '-' }}
            </p>

            <div class="mt-5">
              <h4 class="text-sm font-medium text-n-slate-12">
                {{ $t('GLOSSARY_SETTINGS.TERMS_TITLE') }}
              </h4>
              <div v-if="previewTerms(glossary).length" class="mt-2 grid gap-2">
                <div
                  class="grid grid-cols-2 gap-4 text-xs font-medium text-n-slate-11"
                >
                  <span>{{ $t('GLOSSARY_SETTINGS.FORM.SOURCE_TERM') }}</span>
                  <span>{{ $t('GLOSSARY_SETTINGS.FORM.TARGET_TERM') }}</span>
                </div>
                <div
                  v-for="term in previewTerms(glossary)"
                  :key="`${glossary.id}-${term.source_term}-${term.target_term}`"
                  class="grid grid-cols-2 gap-4 text-sm text-n-slate-12"
                >
                  <span class="min-w-0 truncate">{{ term.source_term }}</span>
                  <span class="min-w-0 truncate">{{ term.target_term }}</span>
                </div>
                <p
                  v-if="remainingTermsCount(glossary)"
                  class="text-sm text-n-slate-11"
                >
                  {{
                    $t('GLOSSARY_SETTINGS.MORE_TERMS', {
                      count: remainingTermsCount(glossary),
                    })
                  }}
                </p>
              </div>
              <div v-else class="mt-2 text-sm text-n-slate-11">
                {{ $t('GLOSSARY_SETTINGS.EMPTY_TERMS') }}
              </div>
            </div>
          </article>
        </div>

        <div
          v-else
          class="mt-5 rounded-xl border border-dashed border-n-weak bg-n-background px-4 py-8 text-center"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('GLOSSARY_SETTINGS.EMPTY') }}
          </p>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ $t('GLOSSARY_SETTINGS.EMPTY_LIST_DESCRIPTION') }}
          </p>
        </div>
      </section>
    </template>

    <CreateGlossaryDialog
      v-if="showCreateDialog"
      ref="createGlossaryDialog"
      :glossaries="records"
      @close="closeCreateDialog"
      @created="handleCreated"
    />
  </SettingsLayout>
</template>
